import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import _ from 'lodash';

import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks';
import {
  chatLoadMoreNotificationData,
  getChatState,
  IChatNotification,
} from '../../../../store/V2/ChatSlice';
import { getShortName } from '../../../../helper';
import {
  adminChatLoadMoreNotificationData,
  getAdminChatState,
} from '../../../../store/V2/AdminChatSlice';

const ChatNotifications: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Store
  const chatState = useAppSelector(getChatState);
  const adminChatState = useAppSelector(getAdminChatState);

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const isAdminChatEnabled = adminChatState.isConnected;
  const isLoadingMore =
    (adminChatState.isConnected &&
      adminChatState.adminChatNotificationData &&
      adminChatState.adminChatNotificationData.isLoadingMore) ||
    (chatState.isConnected &&
      chatState.notificationData &&
      chatState.notificationData.isLoadingMore);

  let totalNotificationListCount = 0;
  let notificationList: IChatNotification[] = [];
  let totalUnseenCount = 0;

  if (
    chatState.isConnected &&
    chatState.notificationData.notificationListData
  ) {
    totalUnseenCount = chatState.notificationData.unSeenCount;
    notificationList =
      chatState.notificationData.notificationListData.list || [];

    totalNotificationListCount =
      chatState.notificationData.notificationListData.totalDocs || 0;
  } else if (
    adminChatState.isConnected &&
    adminChatState.adminChatNotificationData.notificationListData
  ) {
    totalUnseenCount = adminChatState.adminChatNotificationData.unSeenCount;
    notificationList =
      adminChatState.adminChatNotificationData.notificationListData.list || [];

    totalNotificationListCount =
      adminChatState.adminChatNotificationData.notificationListData.totalDocs ||
      0;
  }

  notificationList = _.orderBy(
    [...notificationList],
    ['lastMessageSentAt', 'unseenCount'],
    ['desc', 'desc']
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoadMore = () => {
    if (isAdminChatEnabled) {
      dispatch(
        adminChatLoadMoreNotificationData({
          limit: 50,
          offset: notificationList.length,
        })
      );
    } else {
      dispatch(
        chatLoadMoreNotificationData({
          limit: 50,
          offset: notificationList.length,
        })
      );
    }
  };

  return (
    <div>
      <Badge
        badgeContent={totalUnseenCount}
        color="secondary"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#dd2334',
            minWidth: '18px',
            height: '18px',
            fontSize: '11px',
            right: 3,
            top: 3,
          },
        }}
      >
        <IconButton
          aria-describedby={id}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            backgroundColor: '#E2E5E9',
            padding: '9px',
          }}
        >
          <img
            style={{ width: '20px', height: '20px', color: '#080809' }}
            src={`${process.env.PUBLIC_URL}/images/chat/chat-icon.png`}
            alt="chat-icon"
          />
        </IconButton>
      </Badge>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: '400px',
              borderRadius: '20px',
            },
          },
        }}
      >
        {notificationList.length > 0 ? (
          <List disablePadding sx={{ minWidth: '300px', maxWidth: '100%' }}>
            {notificationList.map((notification, index) => {
              const _conversationCourse = notification.course;
              const _conversationLastMessage = notification.lastMessage;
              const _conversationLastMessageSender =
                _conversationLastMessage && _conversationLastMessage.sender
                  ? _conversationLastMessage.sender
                  : undefined;

              if (
                !_conversationLastMessage ||
                !_conversationCourse ||
                !_conversationLastMessageSender
              ) {
                return null;
              }

              const senderName = getShortName(
                _conversationLastMessageSender.firstname,
                _conversationLastMessageSender.lastname
              );

              const senderAvatar = _conversationLastMessageSender.avatar;

              return (
                <ListItemButton
                  key={index}
                  onClick={() => {
                    navigate(
                      `${process.env.PUBLIC_URL}/portal/courses/${_conversationCourse._id}?conversation=${notification._id}`
                    );

                    handleClose();
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{ width: '35px', height: '35px' }}
                      src={senderAvatar}
                    >
                      {senderName.toUpperCase().slice(0, 1)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Tooltip
                        title={_conversationCourse.title}
                        placement="top"
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight="600"
                          className="ellipsis-text one-line"
                        >
                          {_conversationCourse.title}
                        </Typography>
                      </Tooltip>
                    }
                    secondary={
                      <>
                        <Typography fontSize="14px" component="span">
                          {senderName}&nbsp;:&nbsp;
                        </Typography>
                        {_conversationLastMessage.message}
                      </>
                    }
                  />

                  {notification.unseenCount ? (
                    <>
                      &nbsp;
                      <Box
                        sx={{
                          bgcolor: 'red',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '100%',
                        }}
                      >
                        <Typography color="#ffffff" fontSize="12px">
                          {notification.unseenCount}
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </ListItemButton>
              );
            })}
          </List>
        ) : (
          <Typography fontSize="14px" textAlign="center" padding="20px">
            You have no new notifications
          </Typography>
        )}
        <Collapse in={totalNotificationListCount > notificationList.length}>
          <Stack alignItems="center" sx={{ padding: '0 15px 15px 15px' }}>
            <LoadingButton
              fullWidth
              variant="text"
              size="small"
              color="primary"
              sx={{ maxWidth: '100px' }}
              loading={isLoadingMore}
              onClick={handleLoadMore}
            >
              Load More
            </LoadingButton>
          </Stack>
        </Collapse>
      </Popover>
    </div>
  );
};

export default ChatNotifications;
